import { SquareIconButton } from '@components/SquareIconButton'
import { DeleteTemplateDialog } from '@domains/subscription/priv/TemplateDetails/DeleteTemplateDialog'
import { EditTemplate, type EditTemplateFormData } from '@domains/subscription/priv/TemplateDetails/EditTemplate'
import type { CreateTemplateOptions } from '@domains/subscription/priv/Templates/TemplateForm'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import { Button, Stack, styled, Tab, Tabs, Typography } from '@mui/material'
import FeatherIcon from 'feather-icons-react'
import { useState } from 'react'
import { BillingFrequency, type PricePlanTemplate } from '../types'

export enum TemplateTabs {
  FEATURES = 'features',
  BILLABLES = 'billables',
}

export interface TemplateDetailsProps {
  template: PricePlanTemplate
  navigateBack: () => void
  onDeleteTemplate: (id: string) => void
  onEditTemplate: (data: EditTemplateFormData) => Promise<void>
  onTabChange: (tab: TemplateTabs) => void
  slots: {
    FeaturesTab: React.ReactNode
    BillablesTab: React.ReactNode
  }
  tab: TemplateTabs
  options: CreateTemplateOptions | undefined
}

export const TemplateDetails = ({
  template,
  navigateBack,
  onDeleteTemplate,
  onEditTemplate,
  slots,
  tab,
  onTabChange,
  options,
}: TemplateDetailsProps) => {
  const [selectedTemplateToDelete, setSelectedTemplateToDelete] = useState<PricePlanTemplate | null>(null)
  const [selectedTemplateToEdit, setSelectedTemplateToEdit] = useState<PricePlanTemplate | null>(null)

  return (
    <Stack
      sx={{
        flex: 1,
        gap: 3,
      }}
    >
      <Stack
        sx={{
          gap: 3,
          alignItems: 'stretch',
        }}
      >
        <Stack
          sx={{
            alignItems: 'baseline',
          }}
        >
          <SquareIconButton onClick={navigateBack}>
            <FeatherIcon icon="chevron-left" />
          </SquareIconButton>
        </Stack>
        <Stack
          direction="row"
          sx={{
            flexGrow: 1,
            gap: 3,
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <Stack
            sx={{
              alignItems: 'baseline',
              gap: 0.5,
            }}
          >
            <Typography variant="h1">{template.externalName}</Typography>
            <TemplateIdTypography onClick={() => copyToClipboard(template.id)} variant="p4Regular" color="secondary">
              {template.id}
            </TemplateIdTypography>
            <Typography variant="p4Regular" color="secondary">
              {mapBillingFrequencyToText(template.eligibility.billingFrequency)}
            </Typography>
            <Typography variant="p3Medium" color="textSecondary">
              {template.name}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={() => setSelectedTemplateToEdit(template)}>
              Edit template
            </Button>
            <Button color="error" variant="text" onClick={() => setSelectedTemplateToDelete(template)}>
              Delete
            </Button>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            mb: 2,
          }}
        >
          <Tabs value={tab} onChange={(_, newTab) => onTabChange(newTab)}>
            <Tab label="Features" value={TemplateTabs.FEATURES} />
            <Tab label="Billables" value={TemplateTabs.BILLABLES} />
          </Tabs>
        </Stack>
      </Stack>
      <TabContext value={tab}>
        <Stack
          sx={{
            minHeight: 0,
          }}
        >
          <TabPanel
            sx={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
            value={TemplateTabs.FEATURES}
          >
            {slots.FeaturesTab}
          </TabPanel>
          <TabPanel
            sx={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
            value={TemplateTabs.BILLABLES}
          >
            {slots.BillablesTab}
          </TabPanel>
        </Stack>
      </TabContext>
      {selectedTemplateToDelete && (
        <DeleteTemplateDialog
          open
          onClose={() => setSelectedTemplateToDelete(null)}
          onDelete={() => {
            onDeleteTemplate(selectedTemplateToDelete.id)
            setSelectedTemplateToDelete(null)
          }}
          template={{
            externalName: selectedTemplateToDelete.externalName,
            name: selectedTemplateToDelete.name,
          }}
        />
      )}
      {selectedTemplateToEdit && (
        <EditTemplate
          open
          template={selectedTemplateToEdit}
          onClose={() => setSelectedTemplateToEdit(null)}
          onSave={(formData) => {
            return onEditTemplate(formData)
          }}
          options={options}
        />
      )}
    </Stack>
  )
}

const mapBillingFrequencyToText = (billingFrequency: BillingFrequency | undefined) => {
  switch (billingFrequency) {
    case BillingFrequency.MONTHLY:
      return 'Billed monthly'
    case BillingFrequency.YEARLY:
      return 'Billed yearly'
  }

  return 'Unsupported billing frequency'
}

const copyToClipboard = (text: string) => window.navigator.clipboard.writeText(text)

const TemplateIdTypography = styled(Typography)(() => ({
  cursor: 'pointer',
  transition: 'all 0.1s ease-out',
  ':active': {
    opacity: 0.5,
  },
}))
